import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import * as langs from './Langs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export abstract class Amcharts {
    ref: any;
    chart: any | undefined;
    context: any | undefined;
    indicator: any | undefined;
    fontSize: any | undefined;
    whenReadyFns: any[];
    ready: boolean | undefined;
    defaultLegendTooltipText: string | undefined;
    theme: any | undefined;
    showLabelsFromApp: boolean | undefined;
    colorSetObject!: am4core.ColorSet;

    abstract createByRef(ref: any, context: any): any; // Note: Abstract

    constructor() {
        //console.log("Browser language is " + this.getBrowserLanguage());

        this.chart = null;
        this.indicator = null;
        this.fontSize = 13;
        this.defaultLegendTooltipText = '{name}';
        this.whenReadyFns = [];

        this.colorSetObject = new am4core.ColorSet();
    }

    create(ref: any, chartType: any, context: any) {
        this.ref = ref;
        this.theme = context.theme;
        this.context = context;

        am4core.options.defaultLocale = this.getLang();
        this.chart = am4core.create(ref, chartType);
        this.chart.language.locale = this.getLang();

        let me = this;
        this.chart.events.on('ready', (ev: any) => {
            me.ready = true;

            while (me.whenReadyFns.length) {
                let fn = me.whenReadyFns?.shift();
                fn(ev);
            }
        });
        this.chart.fontSize = this.fontSize;
        this.chart.numberFormatter.numberFormat = (context.settings && context.settings.numberFormat) ? context.settings.numberFormat : "#,###.";

        if (context.settings.numberStandard == "Default") {
            let bigNumberPrefixes = this.getBigNumberPrefixesLocale();
            this.chart.numberFormatter.bigNumberPrefixes = [
                { "number": 1e+3, "suffix": " " + bigNumberPrefixes.thousand },
                { "number": 1e+6, "suffix": " " + bigNumberPrefixes.million },
                { "number": 1e+9, "suffix": " " + bigNumberPrefixes.billion }
            ];
        }
        this.appendDataValidated();
        if (context.title) {
            let titleItem = this.applyTitle(context.title, true);
            this.appendExportProcessing(titleItem);
        }

        if (context.data)
            this.chart.data = context.data;
    }

    recreate(ref: any, context: any) {
        this.dispose();
        this.createByRef(ref, context);
    }

    clearData() {
        if (this.chart) {
            this.chart.data = [];
        }
    }

    dispose() {
        this.chart?.dispose();
    }

    updateSettings(context: any) {
        this.context = context;
    }

    whenReady(fn: any) {
        if (this.ready)
            fn();
        else
            this.whenReadyFns.push(fn);
    }

    appendDataValidated() {
        this.chart.events.on("datavalidated", (_e: any) => {
            if (this.context?.dimensions?.length > 0) {
                this.updateValueInAxes();
            }
        });
    }

    updateValueInAxes() {
        let axes = this.chart.yAxes || this.chart.xAxes;
        if (!axes?.getIndex) return;

        let context = this.context;
        let colors = this.getThemeColors(this.context);
        let valueAxes = axes.getIndex(0)?.className === 'ValueAxis' ? this.chart.yAxes : this.chart.xAxes;

        valueAxes.each((valueAxis: any, i: number) => {
            let isSharedAxis = valueAxes.length == 1;
            let blendSetting = this.getBlendSettingForAxis(valueAxis, context);
            let useBlendSettings = blendSetting && !isSharedAxis;

            let limits = this.getAxisLimits(valueAxis, blendSetting, context, useBlendSettings);
            this.applyAxisFillColor(valueAxis, this.getColorForAxis(valueAxis, colors, i), valueAxes.length);
            this.applyAxisLimits(valueAxis, limits, isSharedAxis);

            if (useBlendSettings) {
                valueAxis.renderer.grid.template.disabled = true;
            }
        });
    }

    getBlendSettingForAxis(valueAxis: any, context: any) {
        return context.blendSettings?.find((b: any) =>
            b.measures.some((m: any) => m.name === valueAxis.dataFields.resultName)
        );
    }

    applyAxisFillColor(valueAxis: any, color: any, totalAxes: number) {
        if (totalAxes > 1) {
            valueAxis.renderer.labels.template.fill = color;
            valueAxis.title.fill = color;
        }
    }

    getColorForAxis(valueAxis: any, colors: any[], fallbackIndex: number): any {
        const seriesValues: any[] = this.chart.series?.values ?? [];
        const series = seriesValues.find((s: any) => s.yAxis === valueAxis);
        if (series) {
            const seriesColor = series.stroke ?? series.fill;
            if (seriesColor) return seriesColor;
        }
        const seriesIndex = seriesValues.findIndex((s: any) => s.yAxis === valueAxis);
        if (seriesIndex >= 0 && seriesIndex < colors.length)
            return colors[seriesIndex];
        return colors[fallbackIndex];
    }

    getAxisLimits(valueAxis: any, blendSetting: any, context: any, useBlendSettings: boolean) {
        let axisMaxValue = useBlendSettings ? blendSetting.axisMaxValue : context.settings.axisMaxValue;
        let axisMinValue = useBlendSettings ? blendSetting.axisMinValue : context.settings.axisMinValue;
        let maxValue = useBlendSettings ? blendSetting.maxValue : context.settings.maxValue;
        let minValue = useBlendSettings ? blendSetting.minValue : context.settings.minValue;
        let isSyncAxesByZero = useBlendSettings ? blendSetting.isSyncAxesByZero : context.settings.isSyncAxesByZero;

        axisMaxValue = this.getScaled(valueAxis, axisMaxValue);
        axisMinValue = this.getScaled(valueAxis, axisMinValue);

        return { axisMaxValue, axisMinValue, maxValue, minValue, isSyncAxesByZero }
    }

    applyAxisLimits(valueAxis: any, limits: any, isSharedAxis: boolean) {
        valueAxis.strictMinMax = isSharedAxis;
        valueAxis.max = limits.axisMaxValue ?? undefined;
        valueAxis.min = limits.axisMinValue ?? undefined;

        if (limits.axisMaxValue == null)
            valueAxis.extraMax = (limits.maxValue ?? 10) / 100.0;

        if (limits.axisMinValue == null && (isSharedAxis || !limits.isSyncAxesByZero))
            valueAxis.extraMin = limits.minValue / 100.0;
    }

    getScaled(valueAxis: any, value: any) {
        return valueAxis.numberFormatter.numberFormat.includes('%') && value != null
            ? value / 100.0
            : value;
    }

    appendExportProcessing(titleItem: any) {
        let isTitleItemDisabled = titleItem.disabled;

        this.chart.exporting.events.on("exportstarted", function (_ev: any) {
            titleItem.disabled = false;
            titleItem.parent.invalidate();
        });
        this.chart.exporting.events.on("exportfinished", (_ev: any) => {
            titleItem.disabled = isTitleItemDisabled;
        });
    }

    applyTitle(title: string, exportOnly: boolean): any {
        let titleItem = this.chart.titles.create();
        titleItem.text = "[bold]" + title + "[/]";
        titleItem.marginTop = 15;
        titleItem.marginBottom = 15;
        titleItem.disabled = exportOnly;

        this.chart.exporting.validateSprites.push(titleItem);
        this.chart.exporting.validateSprites.push(titleItem.parent);

        this.chart.exporting.filePrefix = title;

        return titleItem;
    }

    applyColorSet(settings: any) {
        this.colorSetObject.list = this.getColors(settings);
        this.colorSetObject.startIndex = 0;
        this.chart.colors = this.colorSetObject;
    }

    applyTheme(context: any) {
        let colors = this.getThemeColors(context);

        this.colorSetObject.list = colors;
        this.colorSetObject.reuse = true;
        this.colorSetObject.startIndex = 0;
        this.chart.colors = this.colorSetObject;
    }

    getThemeColors(context: any, measureAppearance: any = null, measureIndex: number | null = null) {
        const settings = context?.settings || {};
        const colors = this.getColors(settings);
        const hasExplicitGraphPalette = context?.theme?.hasExplicitGraphPalette === true;

        const firstDimension = settings?.dimensions?.[0];
        const resolvedTheme = measureAppearance?.theme ?? (measureIndex == null || measureIndex === 0 ? firstDimension?.theme : null);

        const orderedColors = hasExplicitGraphPalette ? colors : this.getOrderedColors(colors, resolvedTheme);

        const useGradient = this.getResolvedUseGradient(context, measureAppearance, measureIndex);
        const gradientStep = this.getResolvedGradientStep(context, measureAppearance, measureIndex);

        if (!useGradient)
            return orderedColors;

        const seriesColor = measureIndex != null && measureIndex >= 0 ? this.getSeriesColor(context, measureAppearance, measureIndex) : null;
        const gradientBaseColor = seriesColor ?? orderedColors[0];
        if (!gradientBaseColor)
            return orderedColors;

        return this.getGradientColors(gradientBaseColor, gradientStep ?? 10);
    }

    getThemeColor(context: any, measureAppearance: any, measureIndex: number | null, colorIndex: number) {
        const colors = this.getThemeColors(context, measureAppearance, measureIndex);
        if (!colors.length)
            return null;

        const parsedIndex = Number(colorIndex);
        const safeIndex = Number.isFinite(parsedIndex) ? Math.max(Math.floor(parsedIndex), 0) : 0;
        const useGradient = this.getResolvedUseGradient(context, measureAppearance, measureIndex);
        const resolvedIndex = useGradient ? Math.min(safeIndex, colors.length - 1) : safeIndex % colors.length;

        return colors[resolvedIndex];
    }

    getSeriesTheme(context: any, measureAppearance: any, measureIndex: number | null) {
        return measureAppearance?.theme ?? (measureIndex === 0 ? context?.settings?.dimensions?.[0]?.theme : null);
    }

    getResolvedUseGradient(context: any, measureAppearance: any, measureIndex: number | null) {
        if (measureAppearance?.theme || measureAppearance?.useGradient === true)
            return measureAppearance?.useGradient === true;

        if (measureIndex == null || measureIndex === 0)
            return context?.settings?.dimensions?.[0]?.useGradient === true;

        return false;
    }

    getResolvedGradientStep(context: any, measureAppearance: any, measureIndex: number | null) {
        if (measureAppearance?.theme || measureAppearance?.useGradient === true)
            return measureAppearance?.gradientStep ?? 10;

        if (measureIndex == null || measureIndex === 0)
            return context?.settings?.dimensions?.[0]?.gradientStep ?? 10;

        return 10;
    }

    hasAnySeriesTheme(context: any) {
        const firstDimension = context?.settings?.dimensions?.[0];
        const measures = context?.settings?.measures ?? [];

        return !!firstDimension?.theme || measures.some((m: any) => !!m?.theme);
    }

    getThemeColorIndex(theme: any) {
        switch (theme) {
            case "secondary": return 1;
            case "tertiary": return 2;
            case "fourthiary": return 3;
            case "fifthiary": return 4;
            case "sixtiary": return 5;
            case "primary":
            default: return 0;
        }
    }

    getSeriesColor(context: any, measureAppearance: any, measureIndex: number | null) {
        const colors = this.getColors(context?.settings || {});
        if (!colors.length) return null;

        const hasExplicitGraphPalette = context?.theme?.hasExplicitGraphPalette === true;
        const getEffectiveTheme = (appearance: any, index: number) => hasExplicitGraphPalette ? null : this.getSeriesTheme(context, appearance, index);
        const theme = measureIndex == null ? null : getEffectiveTheme(measureAppearance, measureIndex);
        if (theme)
            return colors[this.getThemeColorIndex(theme)];

        const measures = context?.settings?.measures ?? [];
        const usedThemeColors = measures
            .map((m: any, i: number) => getEffectiveTheme(m, i))
            .filter(Boolean)
            .map((t: any) => colors[this.getThemeColorIndex(t)]?.toString());

        const availableColors = colors.filter((c: any) => !usedThemeColors.includes(c.toString()));
        const palette = availableColors.length ? availableColors : colors;

        const previousAutoCount = measures
            .slice(0, measureIndex ?? 0)
            .filter((m: any, i: number) => !getEffectiveTheme(m, i))
            .length;

        return palette[previousAutoCount % palette.length];
    }

    applySeriesTheme(series: any, context: any, measureAppearance: any, measureIndex: number | null) {
        if (context?.theme?.hasExplicitGraphPalette === true)
            return;

        if (!measureAppearance?.theme)
            return;

        const color = this.getSeriesColor(context, measureAppearance, measureIndex);
        if (!color) return;

        series.fill = color;
        series.stroke = color;

        if (series.columns?.template) {
            series.columns.template.fill = color;
            series.columns.template.stroke = color;
        }
    }

    getColors(settings: any) {
        let theme = this.theme;
        let isDarkTheme = theme.name == 'Dark';

        if (isDarkTheme) {
            if (settings.useDarkInDarkMode)
                return this.createColors();
            return this.getDefaultDarkColors(theme);
        }

        return this.getPaletteColors(theme);
    }

    createColors() {
        let delta = 0.2;
        let lighten = 0.05;
        let numColors = 5;
        let list = [];
        for (let i = 0; i < numColors; i++) {
            let lightenColor = am4core.color("#27272fff").lighten(lighten);
            list.push(lightenColor);
            lighten += delta;
        }
        return list;
    }

    getOrderedColors(colors: any, theme: any) {
        const index = this.getThemeColorIndex(theme);

        if (!theme || index <= 0 || index >= colors.length)
            return colors;

        const selectedColor = colors[index];
        const orderedColors = colors.filter((_color: any, i: number) => i !== index);

        return [selectedColor, ...orderedColors];
    }
    

    getPaletteColors(theme: any) {
        const raw: string | undefined = theme?.palette?.['__paletteColors'];
        if (!raw) return [];
        const baseColors = raw.split(',').filter(Boolean);
        const lightenValues = [0, 0.3, 0.5, 0.7, 0.9, 0.1];
        const colors: any[] = [];
        for (const lv of lightenValues)
            for (const c of baseColors)
                this.pushColorLighten(colors, c, lv);
        return colors;
    }

    pushColorLighten(arr: any[], colorValue: string | undefined, lighten: number) {
        if (colorValue)
            arr.push(am4core.color(colorValue).lighten(lighten));
    }

    getCustomDarkColors(theme: any) {
        return this.getDefaultDarkColors(theme);
    }

    getGradientColors(color: any, gradientStep: any) {
        const parsedStep = Number(gradientStep);
        const safeStep = Number.isFinite(parsedStep) && parsedStep > 0 ? Math.min(parsedStep, 100) : 10;
        const numberOfSteps = Math.ceil(100 / safeStep);
        const colors = [am4core.color(color)];

        for (let i = 1; i <= numberOfSteps; i++) {
            const lighten = Math.min(i * safeStep / 100, 1);
            colors.push(am4core.color(color).lighten(lighten));
        }

        return colors;
    }

    getDefaultDarkColors(theme: any) {
        const raw: string | undefined = theme?.palette?.['__paletteColors'];
        if (!raw) return [];
        const baseColors = raw.split(',').filter(Boolean);
        const brightenValues = [0, 0.3, 0.5, 0.7, 0.9, 0.1];
        const colors: any[] = [];
        for (const bv of brightenValues)
            for (const c of baseColors)
                this.pushColorBrighten(colors, c, bv);
        return colors;
    }

    pushColorBrighten(arr: any[], colorValue: string | undefined, brighten: number) {
        if (colorValue)
            arr.push(am4core.color(colorValue).brighten(brighten));
    }

    setTheme(theme: any) {
        this.theme = theme;
    }
    getTheme(settings: any) {
        let firstDimension = settings?.dimensions == undefined ? null : settings?.dimensions[0];
        return firstDimension?.theme || 'primary';
    }

    export(exportType: string, context: any) {
        switch (exportType) {
            case "xlsx":
                this.exportExcel(context);
                break;
            case "imageToClipboard":
                this.exportImageToClipboard(context);
                break;
            default:
                this.chart.exporting.export(exportType, context);
        }
    }

    exportExcel(context: any) {
        let workbook = XLSX.utils.book_new();
        workbook.SheetNames.push("Sheet1");

        let dataCopy = JSON.parse(JSON.stringify(this.chart.data));

        this.removeUnderscoreKeysExceptOfDelta(dataCopy);
        this.removeRelatedToDeltaKeys(dataCopy);

        dataCopy = this.replaceFields(dataCopy, context.fields);

        this.parseDateValues(dataCopy);

        const isComparisonMonth = this.context.dimensions.some((e: any) => e.periodComparison == 7) && this.context.dimensions.some((e: any) => e.bin == 5);
        const sheetConfig = isComparisonMonth ? { dateNF: 'MMM', UTC: true } : { UTC: true };
        workbook.Sheets["Sheet1"] = XLSX.utils.json_to_sheet(dataCopy, sheetConfig);

        const workbookFile = XLSX.write(workbook, { bookType: 'xlsx', type: 'binary' });
        saveAs(new Blob([this.s2ab(workbookFile)], { type: "application/octet-stream" }), `${context.title}.xlsx`);
    }

    async exportImageToClipboard(context: any) {
        this.chart.exporting.filePrefix = context.title || "chart";
        this.chart.exporting.useWebFonts = false;

        const titleItem = context.title ? this.applyTitle(context.title, false) : null;
        titleItem?.parent?.invalidate();

        const pngBlobPromise: Promise<Blob> = (async () => {
            try {
                const data = await this.chart.exporting.getImage("png");
                return await (await fetch(data)).blob();
            } finally {
                if (titleItem) {
                    titleItem.disabled = true;
                    titleItem.parent?.invalidate();
                }
            }
        })();

        if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write)
            await navigator.clipboard.write([new ClipboardItem({ "image/png": pngBlobPromise })]);
        else
            saveAs(await pngBlobPromise, `${context.title || "chart"}.png`);
    }

    removeUnderscoreKeysExceptOfDelta(data: any[]) {
        data.forEach((obj: any) => {
            Object.keys(obj).forEach((key: any) => {
                if (key.startsWith("_") && !key.startsWith("_delta")) {
                    delete obj[key];
                }
            });
        });
    }

    removeRelatedToDeltaKeys(data: any[]) {
        data.forEach((obj: any) => {
            let itemsToRemove: string[] = [];

            Object.keys(obj).forEach((key: any) => {
                if (key.startsWith("_delta_")) {
                    const baseName = key.substring(7); // Remove "_delta_"
                    itemsToRemove.push(baseName);
                }
            });

            Object.keys(obj).forEach((key: any) => {
                if (itemsToRemove.some(x => x == key)) {
                    delete obj[key];
                }
            });
        });
    }

    replaceFields(data: any[], fields: any[]) {
        let resultData: any = [];
        data.forEach((obj: any) => {
            if (obj.sliceGrouperOther === true)
                return;

            let str = JSON.stringify(obj);
            fields.forEach((field: any) => {
                const re = new RegExp(`:${field.resultName}`, "g");
                str = str.replace(re, '');
                str = str.replace(this.getSearchValue(field), this.getReplaceValue(field));
            });
            resultData.push(JSON.parse(str));
        });
        return resultData;
    }

    getSearchValue(field: any) {
        //refers to enum { Normal = 0, DeltaValue = 1, DeltaPercent = 2 }
        if (field.valueKind === 1 || field.valueKind === 2) {
            return `_delta_${field.resultName}`;
        }
        return field.resultName;
    }

    getReplaceValue(field: any) {
        if (field.legendTitle) return field.legendTitle;
        if (field.metadataTitle) return field.metadataTitle;

        if (field.valueKind === 1 || field.valueKind === 2) {
            return `Delta of ${field.name}`;
        }
        return field.name;
    }

    parseDateValues(data: any[]) {
        data.forEach((d: any) => {
            for (const [key, value] of (Object as any).entries(d)) {
                let is_date = /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d(?:\.\d+)?Z?/gm;
                if (is_date.test(value))
                    d[key] = new Date(value);
            }
        })
    }

    s2ab(s: any) {
        let buf = new ArrayBuffer(s.length);
        let view = new Uint8Array(buf);

        for (let i = 0; i < s.length; i++)
            view[i] = s.charCodeAt(i) & 0xFF;

        return buf;
    }

    showIndicator(_context: any) {
        this.ref.previousElementSibling.style.display = 'block';
    }

    hideIndicator() {
        this.ref.previousElementSibling.style.display = 'none';
    }

    updateSeries(context?: any) {
        if (context?.settings == null)
            return;

        let settings = context.settings;
        this.chart.series.each((s: any) => { s.stacked = settings.stacked; s.calculatePercent = settings.percentage });
        this.chart.invalidateData();
    }

    removeSerieses() {
        while (this.chart.series.length) {
            this.removeSeries(0);
        }
    }
    removeSeries(index: any) {
        if (this.chart.series.length > index) {
            this.chart.series.removeIndex(index).dispose();
        }
    }

    appendColumnActiveState(series: any) {
        let activeState = series.columns.template.states.create("active");
        let baseColor = new am4core.ColorSet().baseColor;

        activeState.properties.fill = baseColor.lighten(-0.2);
        activeState.propertiesstroke = baseColor.lighten(-0.2);
    }

    onClickColumnHandler(chart: any, series: any, context: any) {
        if (context.disableInteraction)
            return;

        if (!series.columns || !context.onClickColumn)
            return;

        series.columns.template.events.on("hit", function (ev: any) {
            const clickedCategory = ev.target.dataItem.categoryX ?? ev.target.dataItem.categoryY;
            chart.series.each((s: any) => {
                if (!(s instanceof am4charts.ColumnSeries))
                    return;

                s.columns.each((column: any) => {
                    const columnCategory = column.dataItem.categoryX ?? column.dataItem.categoryY;
                    if (columnCategory === clickedCategory) {
                        column.isActive = !column.isActive;
                    }
                });
            });

            const activeContexts: any[] = [];
            chart.series.each((s: any) => {
                if (!(s instanceof am4charts.ColumnSeries)) return;
                s.columns.each((column: any) => {
                    if (column.isActive) {
                        const ctx = JSON.parse(JSON.stringify(column.dataItem.dataContext));
                        ctx.active = true;
                        activeContexts.push(ctx);
                    }
                });
            });

            context.onClickColumn.callee.invokeMethodAsync(context.onClickColumn.function, activeContexts);
        }, this);
    }

    appendThreshold(context: any, series: any, valueFieldName: any, categoryFieldName: any, valueAxis: any = null) {
        let settings = context == null ? null : context.settings;
        let measure = context.isCluster ? settings?.measures[0] : settings?.measures?.find((m: any) => m.name == valueFieldName);
        let valueName = context.isCluster ? measure.name : valueFieldName;

        if (measure?.threshold) {
            this.appendColumnFill(series, measure);

            if (measure.thresholdLine) {
                this.lineRange(valueAxis, measure);
            }
        }

        let dimension = settings?.dimensions?.find((d: any) => d.name == categoryFieldName);
        if (dimension?.threshold?.useThreshold) {
            dimension.threshold.type = 'datetime';
            this.appendDimensionColumnFill(series, dimension);
        }
    }

    setPercentageAxis(valueAxis: any) {
        valueAxis.numberFormatter.numberFormat = "#'%'";
        valueAxis.strictMinMax = true;
        valueAxis.calculateTotals = true;
        valueAxis.renderer.minWidth = 50;
        return valueAxis;
    }

    getValueAxes(): any {
        if (this.chart.xAxes?.getIndex(0).className === 'ValueAxis')
            return this.chart.xAxes;

        if (this.chart.yAxes?.getIndex(0).className === 'ValueAxis')
            return this.chart.yAxes;

        return null;
    }

    createLegend(): any {
        let legend = new am4charts.Legend();
        legend.useDefaultMarker = true;
        return legend;
    }

    updateLegend() {
        if (this.chart.legend) {
            this.chart.legend.invalidateRawData();

            const disableInteraction = this.context?.disableInteraction === true;

            this.chart.legend.itemContainers.template.clickable = !disableInteraction;
            this.chart.legend.itemContainers.template.focusable = !disableInteraction;
            this.chart.legend.itemContainers.template.togglable = !disableInteraction;

            if (disableInteraction) {
                this.chart.legend.itemContainers.template.cursorOverStyle = am4core.MouseCursorStyle.default;
            }
        }
    }

    setLegendAndReorderStacked(context: any) {
        this.setLegend(context);

        if (context.isReorderStacked) {
            let sortedSeries: any = [];
            for (let measure of context.measures) {
                let series = this.chart.series._values.find((x: { dataFields: any; }) => x.dataFields.valueX == measure.name);
                sortedSeries.push(series);
            }
            this.chart.legend.data = sortedSeries;
        }
    }

    setLegend(context: any) {
        let settings = context?.settings;

        if (!settings) return;

        if (!settings.legendPosition || settings.legendPosition == 'none') {
            this.removeLegend();
            return;
        }

        if (!this.chart.legend) this.chart.legend = this.createLegend();

        const isPositionOnSide = settings.legendPosition == 'left' || settings.legendPosition == 'right';
        this.chart.legend.position = settings.legendPosition;
        this.chart.legend.valign = "top";
        this.chart.legend.align = "center";
        this.chart.legend.contentAlign = "center";
        let legendWidth = isPositionOnSide ? 200 : am4core.percent(100);
        this.chart.legend.width = settings.legendWidth > 0 ? settings.legendWidth : legendWidth;
        this.chart.legend.maxHeight = 100;

        if (settings.legendLabelsWidth > 0) {
            this.chart.legend.labels.template.maxWidth = settings.legendLabelsWidth;
            this.chart.legend.labels.template.width = settings.legendLabelsWidth;
        }

        this.chart.legend.labels.template.truncate = settings.legendLabelsTruncate;
        if (!context.disableInteraction) 
            this.chart.legend.itemContainers.template.tooltipText = this.defaultLegendTooltipText;
        this.chart.legend.itemContainers.template.marginBottom = 10;
        this.chart.legend.valueLabels.template.align = "right";
        this.chart.legend.valueLabels.template.textAlign = "end";
        this.chart.legend.scrollable = true;
        this.chart.legend.maxHeight = null;
        this.chart.legend.itemContainers.template.togglable = !context.disableInteraction;
        this.updateLegend();

        if (context.disableInteraction)
            return;

        this.chart.legend.itemContainers.template.events.on("hit", (_ev: any) => {
            let valueAxes = this.getValueAxes();
            if (valueAxes != null) {
                let isSingleValueAxis = valueAxes.length === 1;
                let name = _ev.target.dataItem.name;
                let resultName = _ev.target.dataItem.dataContext.dataFields.resultName;
                let isShowAsPercentage = this.context.measures.some((m: any) => m.showPercentage);
                let clickedSeries = this.chart.series.values.find((s: any) => s.name == name);
                let valueAxis = isSingleValueAxis
                    ? valueAxes.values[0]
                    : (clickedSeries?.yAxis ?? clickedSeries?.xAxis ?? valueAxes.values[0]);
                let series = isSingleValueAxis ? this.chart.series : valueAxis.series;
                let currentSeries = series.values.find((v: any) => v.name == name);
                let visibleSeries = series.values.filter((series: any) => !series.isHidden || (series.name == currentSeries.name && series.isHidden));
                let invisibleSeries = series.values.filter((series: any) => series.isHidden && series.name !== currentSeries.name);
                let isOneVisibleSerie = visibleSeries.length == 1;

                if (isSingleValueAxis)
                    valueAxis.keepSelection = false;

                let min = Number.POSITIVE_INFINITY;
                let max = Number.NEGATIVE_INFINITY;

                visibleSeries.forEach((series: any) => {
                    let { minVal, maxVal } = this.getMinMaxFromData(this.chart.data, this.getSeriesResultName(series))

                    if (minVal !== undefined && isFinite(minVal) && minVal < min)
                        min = minVal;

                    if (maxVal !== undefined && isFinite(maxVal) && maxVal > max)
                        max = maxVal;
                });

                valueAxis.min = isFinite(min) ? min : undefined;
                valueAxis.max = isFinite(max) ? max : undefined;

                if (isShowAsPercentage && isOneVisibleSerie && !currentSeries.ignoreMinMax)
                    valueAxis.extraMin = min / 100.0;

                valueAxis.zoomable = false;
                valueAxis.toggleZoomOutButton = false;
                valueAxis.strictMinMax = false;

                currentSeries.ignoreMinMax = !currentSeries.isHidden;
                for (let invisible of invisibleSeries) {
                    invisible.ignoreMinMax = true;
                }

                if (!isSingleValueAxis) {
                    let valueAxeses = this.chart.yAxes.length > this.chart.xAxes.length ? this.chart.yAxes : this.chart.xAxes;
                    this.baseSyncValueAxes(valueAxeses, valueAxes.values[0]);
                }

                this.chart.invalidateData();

                if (isOneVisibleSerie && !isShowAsPercentage)
                    this.updateValueInAxes();
            }
        });
    }

    getSeriesResultName(currentSeries: any) {
        return currentSeries.dataFields.valueY ?? currentSeries.dataFields.valueX;
    }

    getMinMaxFromData(data: any, resultName: string) {
        let values: number[] = [];
        for (let item of data) {
            let value = item?.[resultName];
            if (typeof value === 'number' && !isNaN(value)) {
                values.push(value);
            }
        }
        const minVal = Math.min(...values);
        const maxVal = Math.max(...values);
        return { minVal, maxVal };
    }

    syncSeries() {
        let valueAxes = this.getValueAxes();
        if (valueAxes != null) {
            let isSingleValueAxis = valueAxes.length === 1;
            let isShowAsPercentage = this.context.measures.some((m: any) => m.showPercentage);

            valueAxes.each((valueAxis: any) => {
                let series = isSingleValueAxis ? this.chart.series : valueAxis.series;
                let visibleSeries = series.values.filter((series: any) => !series.isHidden);
                let invisibleSeries = series.values.filter((series: any) => series.isHidden);
                let isOneVisibleSerie = visibleSeries.length == 1;

                if (isSingleValueAxis)
                    valueAxis.keepSelection = false;

                let min = Number.POSITIVE_INFINITY;
                let max = Number.NEGATIVE_INFINITY;

                visibleSeries.forEach((series: any) => {
                    let minVal = series.dataItem.values["valueY"].low ?? series.dataItem.values["valueX"].low;
                    let maxVal = series.dataItem.values["valueY"].high ?? series.dataItem.values["valueX"].high;

                    if (minVal !== undefined && minVal < min)
                        min = minVal;

                    if (maxVal !== undefined && maxVal > max)
                        max = maxVal;
                });

                valueAxis.min = min;
                valueAxis.max = max;

                if (isShowAsPercentage && isOneVisibleSerie)
                    valueAxis.extraMin = min / 100.0;

                valueAxis.zoomable = false;
                valueAxis.toggleZoomOutButton = false;
                valueAxis.strictMinMax = false;

                for (let invisible of invisibleSeries) {
                    invisible.ignoreMinMax = true;
                }

                if (!isSingleValueAxis) {
                    let valueAxeses = this.chart.yAxes.length > this.chart.xAxes.length ? this.chart.yAxes : this.chart.xAxes;
                    this.baseSyncValueAxes(valueAxeses, valueAxes.values[0]);
                }

                this.chart.invalidateData();

                if (isOneVisibleSerie && !isShowAsPercentage)
                    this.updateValueInAxes();
            });
        }
    }

    removeLegend() {
        if (this.chart.legend) {
            this.chart.legend.dispose();
            this.chart.legend = undefined;
        }
    }

    getMeasureNumberFormat(measure: any, settings: any): any {
        let visualFormat = settings.numberFormat ?? "#,###.";
        let numberFormat = measure?.numberFormat && measure?.numberFormat !== "not_set"
            ? measure?.numberFormat
            : settings.numberFormat ?? "#,###.";
        return numberFormat == "Default" ? visualFormat : numberFormat;
    }

    getMeasureFixedBigNumberFormat(measure: any, settings: any): any {
        let visualFormat = settings.fixedBigNumberFormat ?? -1;
        let numberFormat = measure?.fixedBigNumberFormat && measure?.fixedBigNumberFormat !== -1
            ? measure?.fixedBigNumberFormat
            : settings.fixedBigNumberFormat ?? -1;
        return numberFormat == -1 ? visualFormat : numberFormat;
    }

    getDimensionNumberFormat(dimension: any): any {
        let numberFormat = dimension?.numberFormat ?? "#.";
        return numberFormat === "Default" ? "#." : numberFormat;
    }

    getSiFormat() {
        return { thousand: "T", million: "M", billion: "G" }
    }

    getBigNumberPrefixes(measureAppearance: any, settings: any) {
        let bigNumberPrefixes = this.getSiFormat();

        if ((measureAppearance?.numberFormat !== "not_set" && measureAppearance?.numberStandard === "Default") || (settings?.numberFormat !== "" && settings?.numberStandard === "Default"))
            bigNumberPrefixes = this.getBigNumberPrefixesLocale();

        return [
            { "number": 1e+3, "suffix": " " + bigNumberPrefixes.thousand },
            { "number": 1e+6, "suffix": " " + bigNumberPrefixes.million },
            { "number": 1e+9, "suffix": " " + bigNumberPrefixes.billion }
        ];
    }

    lineRange(valueAxis: any, measure: any) {
        if (!(valueAxis && measure.thresholdCustomValue && measure.thresholdColor))
            return;

        let range = valueAxis.axisRanges.create();
        range.value = measure.thresholdCustomValue;
        range.grid.stroke = am4core.color(measure.thresholdColor);
        range.grid.strokeWidth = 2;
        range.grid.strokeOpacity = 1;
        range.grid.above = true;
    }

    appendRanges(valueAxis: any, series: any, measure: any) {
        let range = valueAxis.createSeriesRange(series);
        range.value = 0;
        range.endValue = measure.thresholdCustomValue;
        range.contents.stroke = am4core.color(measure.thresholdColor);
        range.contents.fill = range.contents.stroke;
    }

    appendColumnFill(series: any, measure: any) {
        let me = this;

        if (measure.thresholdType === 'datetime') {
            series.columns.template.adapter.add("fill", (fill: any, target: any) => me.datetimeColumnFill(fill, target, measure));
            series.columns.template.adapter.add("stroke", (stroke: any, target: any) => me.datetimeColumnFill(stroke, target, measure));
        }
        else if (measure.thresholdType === 'custom') {
            series.columns.template.adapter.add("fill", (fill: any, target: any) => me.customColumnFill(fill, target, measure));
            series.columns.template.adapter.add("stroke", (stroke: any, target: any) => me.customColumnFill(stroke, target, measure));
        }
        else {
            series.columns.template.adapter.add("fill", (fill: any, target: any) => me.negativeColumnFill(fill, target, measure));
            series.columns.template.adapter.add("stroke", (stroke: any, target: any) => me.negativeColumnFill(stroke, target, measure));
        }
    }

    appendDimensionColumnFill(series: any, dimension: any) {
        let me = this;

        if (dimension.threshold.type === 'datetime') {
            series.columns.template.adapter.add("fill", (fill: any, target: any) => me.datetimeDimensionColumnFill(fill, target, dimension));
            series.columns.template.adapter.add("stroke", (stroke: any, target: any) => me.datetimeDimensionColumnFill(stroke, target, dimension));
            series.columns.template.adapter.add("fillOpacity", (fillOpacity: any, target: any) => me.datetimeDimensionColumnOpacity(fillOpacity, target, dimension));
        }
    }

    datetimeDimensionColumnFill(fill: any, target: any, dimension: any) {
        let date = new Date(dimension.threshold.selectedDate);
        let chartDate = target.dataItem?.categoryX ?? target.dataItem?.categoryY;
        let targetDate = new Date(chartDate);
        date.setHours(0, 0);
        if (!target.dataItem || targetDate < date)
            return fill;

        return dimension.threshold.colorOpacity ? fill : am4core.color(dimension.threshold.color);
    }

    datetimeDimensionColumnOpacity(fillOpacity: any, target: any, dimension: any) {
        let date = new Date(dimension.threshold.selectedDate);
        let chartDate = target.dataItem?.categoryX ?? target.dataItem?.categoryY;
        let targetDate = new Date(chartDate);
        date.setHours(0, 0);
        if (!target.dataItem || targetDate < date)
            return fillOpacity;

        return dimension.threshold.colorOpacity ? 0.4 : fillOpacity;
    }

    datetimeColumnFill(fill: any, target: any, measure: any) {
        let date = new Date(measure.thresholdSelectedDate);
        date.setHours(0, 0);
        if (!target.dataItem || target.dataItem.dateX < date)
            fill.alpha = 1;

        return fill;
    }

    negativeColumnFill(fill: any, target: any, measure: any) {
        const dataValue = target.dataItem?.valueY ?? target.dataItem?.valueX;
        if (dataValue >= 0) return fill;

        return am4core.color(measure.thresholdColor);
    }

    customColumnFill(fill: any, target: any, measure: any) {
        const dataValue = target.dataItem?.valueY ?? target.dataItem?.valueX;
        if (dataValue >= measure.thresholdCustomValue || !target.dataItem)
            return fill;

        return am4core.color(measure.thresholdColor);
    }

    appendLineFill(series: any, measure: any) {
        const datetimeLineFill = (fill: any, target: any) => {
            let date = new Date(measure.thresholdSelectedDate);
            date.setHours(0, 0);
            if (target.dataItem && (target.dataItem.dateX >= date))
                return am4core.color(measure.thresholdColor);
            else
                return fill;
        };

        const lineFill = datetimeLineFill;
        if (lineFill)
            series.segments.template.adapter.add("stroke", lineFill);
    }

    getLabelCenter(rotation: number): { horizontalCenter: string, verticalCenter: string } {
        switch (rotation) {
            case 90:
                return { horizontalCenter: "top", verticalCenter: "middle" };
            case -90:
                return { horizontalCenter: "right", verticalCenter: "middle" };
            case 45:
                return { horizontalCenter: "left", verticalCenter: "middle" };
            case -45:
                return { horizontalCenter: "right", verticalCenter: "middle" };
            default:
                return { horizontalCenter: "middle", verticalCenter: "middle" };
        }
    }

    markActiveByValue(_values: any[]) {
        return;
    }

    getMarkedMultipleAxes(valueAxes: any, measureSettingsWithOwnAxes: any, options?: any): any[] {
        const ownAxes = measureSettingsWithOwnAxes;
        const defaultMode = "general";
        const mode: string = options?.mode ?? defaultMode;

        return valueAxes?.values?.map((axis: any) => {
            const filteredOwnAxes = ownAxes?.filter((ownAxis: any) => axis.dataFields.resultName === ownAxis.name);
            const areAxesExist = filteredOwnAxes?.length || options?.measuresSettingsWithDeltaAxes?.filter((deltaAxis: any) => axis.dataFields.resultName === deltaAxis.resultName)?.length;

            if (areAxesExist)
                return mode === defaultMode ? axis : null;
            else
                return mode === defaultMode ? null : axis;
        });
    }

    getActiveAxis(valueAxes: any, valueFieldName: any) {
        const activeAxis = valueAxes?.values?.find((axis: any) =>
            axis.dataFields.resultName === valueFieldName || axis.dataFields.deltaName === valueFieldName);

        if (activeAxis !== null && activeAxis !== undefined)
            return activeAxis;

        return valueAxes?.values[0];
    }

    baseSyncValueAxes(valueAxes: any, mainAxis: any) {
        let maxValue = this.context.settings?.maxValue ?? 10;
        if (valueAxes.length === 1) {
            valueAxes.values[0].syncWithAxis = undefined;
            this.setExtraMaxForAxis(valueAxes.values[0], maxValue);
            valueAxes.values[0].keepSelection = true;
            return;
        }
        if (mainAxis !== undefined && mainAxis !== null) {
            mainAxis.syncWithAxis = undefined;
            this.setExtraMaxForAxis(mainAxis, maxValue);
            mainAxis.keepSelection = true;
            valueAxes.each((valueAxis: any) => {
                if (mainAxis !== valueAxis) {
                    const isOwnAxis = this.context.settings?.measures?.some(
                        (m: any) => m.hasOwnAxis && (m.name === valueAxis.dataFields.name
                            || m.resultName === valueAxis.dataFields.resultName)
                    );
                    if (!isOwnAxis) {
                        valueAxis.keepSelection = false;
                        this.setExtraMaxForAxis(valueAxis, maxValue);
                        valueAxis.syncWithAxis = mainAxis;
                    }
                }
            });
        }
    }

    setExtraMaxForAxis(axis: any, maxValue: any) {
        if (this.context.settings?.axisMaxValue == null)
            axis.extraMax = maxValue / 100.0;
    }

    getSeriesName(measure: any) {
        if (measure?.name === measure?.resultName)
            return measure?.name;
        else
            return measure?.name || "Count";
    }

    baseGetMinValueForDeltaAxes(context: any): any {
        let data = context.data;
        let min = Infinity;

        if (data?.length) {
            let keys = Object.keys(data?.[0]);
            for (let key in keys) {
                let prop = keys[key];
                if (prop.includes('_delta_value') && !prop.includes('disabled')) {
                    let currentValues: any = [];
                    for (let d in data) {
                        let value = data[d];
                        currentValues.push(value[prop]);
                    }
                    let localMin = Math.min(...currentValues);
                    if (localMin < min)
                        min = localMin;
                }
            }
        }

        return min;
    }

    baseGetMaxValueForDeltaAxes(context: any): any {
        let data = context.data;
        let max = -Infinity;

        if (data?.length) {
            let keys = Object.keys(data?.[0]);
            for (let key in keys) {
                let prop = keys[key];
                if (prop.includes('_delta_value') && !prop.includes('disabled')) {
                    let currentValues: any = [];
                    for (let _key in data) {
                        let value = data[_key];
                        currentValues.push(value[prop]);
                    }
                    let localMax = Math.max(...currentValues);
                    if (localMax > max)
                        max = localMax;
                }
            }
        }

        return max;
    }

    baseSyncAxesByZero(context: any, axes: any) {
        if (!context.settings.isSyncAxesByZero) {
            axes.values?.forEach((axis: any) => {
                axis.min = undefined;
                axis.max = undefined;
            });
            return;
        }

        const data = context.data;
        if (!data || axes.values?.length <= 1)
            return;

        const allValues = this.getAllValues(data, axes);
        const globalMin = Math.min(...allValues);
        const globalMax = Math.max(...allValues);

        axes.values?.forEach((axis: any) => {
            const currentValues = this.getValuesByProp(
                data,
                axis.dataFields.hasDelta ? axis.dataFields.deltaName : axis.dataFields.resultName
            );
            if (globalMin >= 0) {
                const max = Math.max(...currentValues);
                axis.max = max;
                axis.min = 0;
            } else if (globalMax <= 0) {
                const min = Math.min(...currentValues);
                axis.max = 0;
                axis.min = min;
            } else {
                const absMax = Math.max(...currentValues.map((value: any) => Math.abs(value)));
                axis.max = absMax;
                axis.min = -absMax;
            }
        });
    }

    getValuesByProp(data: any, prop: any) {
        let currentValues: any = [];
        for (let key in data) {
            let value = data[key];
            currentValues.push(value[prop]);
        }
        return currentValues;
    }

    getAllValues(data: any, axes: any) {
        let allValues: any = [];
        for (let key in data) {
            let value = data[key];
            axes.values?.forEach((axis: any) => {
                allValues.push(value[axis.dataFields.hasDelta ? axis.dataFields.deltaName : axis.dataFields.resultName]);
            });
        }
        return allValues;
    }

    getActiveLanguage() {
        const ctx = this.context;

        const uiLang = ctx?.currentLanguage?.key;
        if (uiLang && uiLang.length > 0) return uiLang;

        const appLang = ctx?.applicationLanguage;
        if (appLang && appLang.length > 0) return appLang;

        return this.getBrowserLanguage();
    }

    getBrowserLanguage() {
        return navigator.language || (navigator?.languages?.length && navigator.languages[0]) || 'en'
    }

    getLang() {
        switch (this.getActiveLanguage()) {
            case "no": return langs.no.default;
            case "nb": return langs.no.default;
            case "nn": return langs.no.default;
            case "sv": return langs.sv.default;
            case "sv-SE": return langs.sv.default;
            case "fi": return langs.fi.default;
            case "da": return langs.da.default;
            case "es": return langs.es.default;
            case "cs": return langs.cs.default;
            case "sk": return langs.sk.default;
            default: return langs.en.default;
        }
    }

    getLanguageAbbreviation(language: any) {
        switch (language.includes('nb')) {
            case 'nn':
            case 'no':
            case 'nb':
                return 'nb';
            case 'sv':
            case 'sv-SE':
                return 'sv';
            case 'ru':
                return 'ru';
            default:
                return "default";
        }
    }

    getBigNumberPrefixesLocale() {
        let language = this.getActiveLanguage() ?? this.getLanguageAbbreviation(this.getActiveLanguage());
        switch (language) {
            case 'nn':
            case 'no':
            case 'nb':
                return { billion: 'mrd.', million: 'mill.', thousand: 'T' };
            case 'sv':
            case 'sv-SE':
                return { billion: 'mdr', million: 'm', thousand: 'T' };
            case 'da':
                return { billion: 'mia.', million: 'mio.', thousand: 'T' };
            case 'fi':
                return { billion: 'mrd.', million: 'milj.', thousand: 'T' };
            case 'cs':
                return { billion: 'mld.', million: 'mil.', thousand: 'tis. ' };
            case 'es':
                return { billion: 'Bn', million: 'M', thousand: 'K' };
            case 'sk':
                return { billion: 'mld.', million: 'mil.', thousand: 'tis. ' };
            case 'ru':
                return { billion: 'млрд', million: 'млн', thousand: 'тыс' };
            default:
                return { billion: 'B', million: 'M', thousand: 'T' };
        }
    }

    setLabelsStatus(showLabels: boolean) {
        this.showLabelsFromApp = showLabels;
    }

    isNeedToShowLabels(showLabelsFromApp: any, showLabelsByDefault: any) {
        return showLabelsFromApp === undefined ? showLabelsByDefault : showLabelsFromApp;
    }

    percentSignIfNeeded(numberFormat: any, measure: any, canUseDelta: any, basicMeasures: any, isCluster: any, i: any) {
        let isDeltaPercent = measure?.valueKind == 2;
        if (numberFormat?.endsWith('%')) return "";
        if (isCluster && i !== undefined && basicMeasures.length > 0 && basicMeasures[i]?.valueKind == 2) return "%";
        if (canUseDelta && isDeltaPercent) return "%";
        return "";
    }

    showValueAxisTooltip(valueAxis: any, shouldShowTooltip: any) {
        if (!shouldShowTooltip)
            valueAxis.cursorTooltipEnabled = false;
    }

    shouldUseCursor(context: any) {
        return context.settings.showMultiTooltips || context.settings.showValueAxisTooltip || (context.settings?.stacked && context.settings.showLegendPercent);
    }

    shouldPrefixLegendNameInTooltip(context: any): boolean {
        const settings = context?.settings;
        if (!settings) return false;
        const measuresCount = context.measures?.length ?? 0;
        const dimensionsCount = context.dimensions?.length ?? 0;
        return measuresCount > 1 || dimensionsCount > 1;
    }

    translate(model: any, propName: string, lang: string) {
        let propValue = model?.[`${propName}`];
        let glossary = model?.[`${propName}Glossary`];
        let translation = glossary?.translations?.keywords?.find((x: any) => x.language === lang);
        return translation?.text ?? propValue;
    }

    translateFromSettings(context: any, propName: string) {
        return this.translate(context?.settings, propName, context?.currentLanguage?.key);
    }

    translateFromMeasure(context: any, measureAppearance: any, propName: string) {
        return this.translate(measureAppearance, propName, context?.currentLanguage?.key);
    }

    traslateFromDimension(context: any, dimensionAppearance: any, propName: string) {
        return this.translate(dimensionAppearance, propName, context?.currentLanguage?.key);
    }

    getPrefix(context: any, measureAppearance: any, settings: any) {
        let measurePrefix = this.translateFromMeasure(context, measureAppearance, "prefix");
        if (measurePrefix)
            return `${measurePrefix} `;

        let settingsPrefix = this.translateFromSettings(context, "prefix");
        if (settingsPrefix)
            return `${settingsPrefix} `;

        return "";
    }

    getSuffix(context: any, measureAppearance: any, settings: any) {
        let measureSuffix = this.translateFromMeasure(context, measureAppearance, "suffix");
        if (measureSuffix)
            return ` ${measureSuffix}`;

        let settingsSuffix = this.translateFromSettings(context, "suffix");
        if (settingsSuffix)
            return ` ${settingsSuffix}`;

        return "";
    }

    dateCategoryFormatter(label: any) {
        let rollingGrouping = this.context?.settings?.measures?.find((x: any) => x.comparisonType === 3);
        let dateTimeDimension = this.context?.dimensions?.find((x: any) => x.dataType == 3);

        if (label && dateTimeDimension) {
            let labelWithoutBracets = label.replace('{', '').replace('}', '');
            let bin = rollingGrouping?.transformationSettings?.groupedBin ?? dateTimeDimension.bin;
            let formatted = `{${labelWithoutBracets}.formatDate('${this.getDateTimeFormat(bin)}')}`;
            if (bin === 6) {
                return `Q${formatted}`;
            }
            return `${formatted}`;
        }
        return `{${label}}`;
    }

    getDateTimeFormat(bin: number) {
        switch (bin) {
            case 1:
                return 'dd.MMM yyyy H:mm';
            case 2:
                return 'dd.MMM yyyy H:00';
            case 3:
                return 'dd.MMM yyyy';
            case 4:
                return 'w, yyyy';
            case 5:
                return 'MMM yyyy';
            case 6:
                return 'q, yyyy';
            default:
                return 'yyyy';
        }
    }

    setFixedBigNumberValueAxis(valueAxis: any, measureAppearance: any, settings: any) {
        let fixedBigNumberFormat = this.getMeasureFixedBigNumberFormat(measureAppearance, settings);
        let numberFormat = this.getMeasureNumberFormat(measureAppearance, settings);

        if (this.isFixedBigNumberFormat(fixedBigNumberFormat, numberFormat)) {
            const decimalPart = numberFormat.split('.')[1]?.split('a')[0];
            const precision: number = decimalPart ? decimalPart.length : 0;
            let language = this.getActiveLanguage() ?? this.getLanguageAbbreviation(this.getActiveLanguage());

            valueAxis.renderer.labels.template.adapter.add("text", (label: any, target: any) => {
                if (target?.dataItem?.value) {
                    let number = target.dataItem.value / fixedBigNumberFormat;
                    let formatter = new Intl.NumberFormat(language, {
                        minimumFractionDigits: precision,
                        maximumFractionDigits: precision,
                    });

                    let bigNumberPrefix = this.getBigNumberPrefix(measureAppearance, settings);
                    return formatter.format(number) + " " + bigNumberPrefix;
                }
                return label;
            });
        }
    }

    setLabelVisibility(valueAxis: any, settings: any) {
        valueAxis.renderer.labels.template.disabled = settings.hideAxisLabels ?? false;
    }

    setCustomLabelsIfNeeded(valueAxis: any, measureAppearance: any) {
        if (measureAppearance?.customLabels?.length > 0) {
            valueAxis.renderer.grid.template.disabled = true;
            valueAxis.renderer.labels.template.disabled = true;

            measureAppearance.customLabels.forEach((customLabel: any) => {
                this.createRangeValueAxis(valueAxis, customLabel.value, customLabel.label);
            });
        }
    }

    createRangeValueAxis(valueAxis: any, value: any, replaceTo: any) {
        var range = valueAxis.axisRanges.create();
        range.value = value;
        range.label.text = replaceTo;
        range.label.fontSize = 14;
        range.label.fontWeight = "bold";
        range.grid.strokeOpacity = 0;
    }

    setFixedBigNumberLabel(labelBullet: any, measureAppearance: any, settings: any) {
        let fixedBigNumberFormat = this.getMeasureFixedBigNumberFormat(measureAppearance, settings);
        let numberFormat = this.getMeasureNumberFormat(measureAppearance, settings);

        if (this.isFixedBigNumberFormat(fixedBigNumberFormat, numberFormat)) {
            const decimalPart: string | undefined = numberFormat.split('.')[1]?.split('a')[0];
            const precision: number = decimalPart ? decimalPart.length : 0;
            let language = this.getActiveLanguage() ?? this.getLanguageAbbreviation(this.getActiveLanguage());
            let prefix = this.getPrefix(this.context, measureAppearance, settings);
            let suffix = this.getSuffix(this.context, measureAppearance, settings);

            labelBullet.adapter.add("text", (label: any, target: any) => {
                let number = (target.dataItem.valueX ?? target.dataItem.valueY) / fixedBigNumberFormat;
                let formatter = new Intl.NumberFormat(language, {
                    minimumFractionDigits: precision,
                    maximumFractionDigits: precision,
                });
                let bigNumberPrefix = this.getBigNumberPrefix(measureAppearance, settings);
                if (target.dataItem && (target.dataItem.valueX ?? target.dataItem.valueY))
                    return prefix + formatter.format(number) + " " + bigNumberPrefix + suffix;

                return label;
            });
        }
    }

    setFixedBigNumberTooltip(series: any, measureAppearance: any, settings: any) {
        let fixedBigNumberFormat = this.getMeasureFixedBigNumberFormat(measureAppearance, settings);
        let numberFormat = this.getMeasureNumberFormat(measureAppearance, settings);

        if (this.isFixedBigNumberFormat(fixedBigNumberFormat, numberFormat)) {
            const decimalPart: string | undefined = numberFormat.split('.')[1]?.split('a')[0];
            const precision: number = decimalPart ? decimalPart.length : 0;
            let language = this.getActiveLanguage() ?? this.getLanguageAbbreviation(this.getActiveLanguage());
            let prefix = this.getPrefix(this.context, measureAppearance, settings);
            let suffix = this.getSuffix(this.context, measureAppearance, settings);

            series.tooltip.label.adapter.add("text", (label: any, target: any) => {
                let number = (target.dataItem.valueX ?? target.dataItem.valueY) / fixedBigNumberFormat;
                let formatter = new Intl.NumberFormat(language, {
                    minimumFractionDigits: precision,
                    maximumFractionDigits: precision,
                });

                let dataItem = target.dataItem;
                let bigNumberPrefix = this.getBigNumberPrefix(measureAppearance, settings);

                if (dataItem && (dataItem.valueX ?? dataItem.valueY))
                    return series.name + ": " + prefix + formatter.format(number) + " " + bigNumberPrefix + suffix;
                return label;
            });
        }
    }

    isFixedBigNumberFormat(fixedBigNumberFormat: any, numberFormat: any) {
        return fixedBigNumberFormat != -1 && numberFormat.includes("a");
    }

    getBigNumberPrefix(measureAppearance: any, settings: any) {
        let bigNumberPrefixes = this.getSiFormat();
        if ((measureAppearance?.numberFormat !== "not_set" && measureAppearance?.numberStandard === "Default") || (settings?.numberFormat !== "" && settings?.numberStandard === "Default"))
            bigNumberPrefixes = this.getBigNumberPrefixesLocale();

        switch (this.getMeasureFixedBigNumberFormat(measureAppearance, settings)) {
            case 1e+6:
                return bigNumberPrefixes.million;
            case 1e+9:
                return bigNumberPrefixes.billion;
            default:
                return bigNumberPrefixes.thousand;
        }
    }

    setCornerRadius(useCornerRadius: boolean, column: any) {
        if (useCornerRadius) {
            column.adapter.add("cornerRadiusTopLeft", (radius: any, target: any) => (target.dataItem && (target.dataItem.valueY < 0)) ? 0 : 4);
            column.adapter.add("cornerRadiusTopRight", (radius: any, target: any) => (target.dataItem && (target.dataItem.valueY < 0)) ? 0 : 4);
            column.adapter.add("cornerRadiusBottomLeft", (radius: any, target: any) => (target.dataItem && (target.dataItem.valueY > 0)) ? 0 : 4);
            column.adapter.add("cornerRadiusBottomRight", (radius: any, target: any) => (target.dataItem && (target.dataItem.valueY > 0)) ? 0 : 4);

            //series.columns.template.column.cornerRadius(4, 4, 0, 0);
        }
    }

    isDimensionalThreshold(dimensionAppearance: any) {
        if (dimensionAppearance?.threshold?.useThreshold) {
            if (dimensionAppearance?.threshold?.selectedDate)
                return true;
            else
                return false;
        } else
            return false;
    }

    isMeasureCustomThreshold(measureAppearance: any) {
        return measureAppearance?.threshold && measureAppearance?.thresholdType === 'custom' && measureAppearance?.thresholdCustomValue !== null;
    }

    isMeasureNegativeThreshold(measureAppearance: any) {
        return measureAppearance?.threshold && measureAppearance?.thresholdType === 'negative';
    }

    isThresholdColorDotted(measureAppearance: any) {
        return measureAppearance?.dimensionThreshold?.colorDotted;
    }

    isThresholdColorOpacity(measureAppearance: any) {
        return measureAppearance?.dimensionThreshold?.colorOpacity;
    }

    getThresholdColor(measureAppearance: any, series: any) {
        if (!measureAppearance?.dimensionThreshold?.useSeriesColor) {
            return measureAppearance?.dimensionThreshold?.color;
        }

        //return dimensionAppearance.threshold.color;

        if (series.properties.stroke)
            return series.properties.stroke;

        return series.propertyFields.stroke;
    }

    getBulletColor(measureAppearance: any, series: any) {
        if (measureAppearance?.useSeriesColorForBullet) {
            if (series.properties.stroke) {
                return series.properties.stroke;
            }
            return series.propertyFields.stroke;
        }
        else {
            return measureAppearance?.bulletColor ?? am4core.color("rgba(0,0,0,0)");
        }
    }

    getLabelDistanceOffset(distance: string): number {
        const defaultValue = 10;
        const smallValue = 0;
        const largeValue = 20;

        switch (distance) {
            case "small":
                return smallValue;
            case "medium":
                return defaultValue;
            case "large":
                return largeValue;
            default:
                return defaultValue;
        }
    }

    getDistance(bulletRadius: any, offsetValue: any) {
        let lineGap = 4;
        return bulletRadius + offsetValue - lineGap;
    }

    addLineX1Adapter(line: any, bulletRadius: number, isHorizontal: boolean = false) {
        line.adapter.add("x1", (x1: any, target: any) => {
            if (isHorizontal) {
                let value = target.parent?.dataItem?.values?.valueX?.value;

                if (value == null)
                    return x1;

                return value > 0 ? bulletRadius : -bulletRadius;
            }
            return 0;
        });
    }

    addLineY1Adapter(line: any, bulletRadius: number, isHorizontal: boolean = false) {
        line.adapter.add("y1", (y1: any, target: any) => {
            if (isHorizontal)
                return 0;

            let value = target.parent?.dataItem?.values?.valueY?.value;

            if (value == null)
                return y1;

            return value > 0 ? -bulletRadius : bulletRadius;
        });
    }

    addLineX2Adapter(line: any, bulletRadius: number, offsetValue: number, isHorizontal: boolean = false) {
        line.adapter.add("x2", (x2: any, target: any) => {
            if (isHorizontal) {
                let value = target.parent?.dataItem?.values?.valueX?.value;
                let distance = this.getDistance(bulletRadius, offsetValue);

                if (value == null)
                    return x2;

                return value > 0 ? distance : -distance;
            }
            return 0;
        });
    }

    addLineY2Adapter(line: any, bulletRadius: number, offsetValue: number, isHorizontal: boolean = false) {
        line.adapter.add("y2", (y2: any, target: any) => {
            if (isHorizontal)
                return 0;

            let value = target.parent?.dataItem?.values?.valueY?.value;
            let distance = this.getDistance(bulletRadius, offsetValue);

            if (value == null)
                return y2;

            return value > 0 ? -distance : distance;
        });
    }

    getTextPrimaryColor(): string {
        let host: any = this.ref instanceof HTMLElement ? this.ref : document.documentElement;
        let textPrimary = getComputedStyle(host).getPropertyValue('--text-primary').trim();
        let mudTextPrimary = getComputedStyle(host).getPropertyValue('--mud-palette-text-primary').trim();

        return textPrimary || mudTextPrimary || "#464646ff";
    }

    getDefaultLabelColor(): string {
        return this.getTextPrimaryColor();
    }

    addLineStrokeAdapter(line: any, measureAppearance: any) {
        line.adapter.add("stroke", (stroke: any, target: any) => {
            return am4core.color(measureAppearance?.labelColor || this.getDefaultLabelColor());
        });
    }

    addLabelPositionAdapter(label: any, isInside: boolean, isHorizontal: boolean = false) {
        let adapterName = isHorizontal ? "horizontalCenter" : "verticalCenter";
        let straight = isHorizontal ? "right" : "top";
        let opposite = isHorizontal ? "left" : "bottom";
        let valueField = isHorizontal ? 'valueX' : 'valueY';

        label.adapter.add(adapterName, (center: any, target: any) => {
            const value = target.dataItem?.values?.[valueField]?.value;
            if (value == null) return center;

            if (isInside) {
                return value > 0 ? straight : opposite;
            } else {
                return value > 0 ? opposite : straight;
            }
        });
    }

    addLabelDxAdapter(label: any, isInside: boolean, offsetValue: number, totalOffset: number, valueField: string = 'valueX') {
        label.adapter.add("dx", (dx: any, target: any) => {
            const value = target.dataItem?.values?.[valueField]?.value;
            if (value == null) return dx;

            if (isInside) {
                return value > 0 ? -offsetValue : offsetValue;
            } else {
                return value > 0 ? totalOffset : -totalOffset;
            }
        });
    }

    addLabelDyAdapter(label: any, isInside: boolean, offsetValue: number, totalOffset: number, valueField: string = 'valueY') {
        label.adapter.add("dy", (dy: any, target: any) => {
            const value = target.dataItem?.values?.[valueField]?.value;
            if (value == null) return dy;

            if (isInside) {
                return value > 0 ? offsetValue : -offsetValue;
            } else {
                return value > 0 ? -totalOffset : totalOffset;
            }
        });
    }

    createLabelLine(labelBullet: any, measureAppearance: any, showLines: boolean, isInside: boolean) {
        if (showLines && !isInside) {
            if (!labelBullet.line) {
                labelBullet.line = labelBullet.createChild(am4core.Line);
            }

            labelBullet.line.strokeWidth = 2;

            this.addLineStrokeAdapter(labelBullet.line, measureAppearance);
        }
    }
}
